(() => {
  if (customElements.get('bsb-page-embed')) return;

  // Only what styles the page is carried over from its head; scripts (analytics, apps, chat) stay out.
  const HEAD_SELECTOR = [
    'meta[charset]',
    'meta[name="viewport"]',
    'link[rel="stylesheet"]',
    'link[rel="preconnect"]',
    'link[rel="preload"][as="font"]',
    'style'
  ].join(',');

  /*
    The frame is a viewport-sized window pinned below the header, so the embedded page keeps its sticky and
    viewport-unit layout. It never scrolls on its own: the section is as tall as the embedded content, and as the
    home page scrolls through it that progress is mirrored inside the frame, leaving a single scroll.
  */
  class BSBPageEmbed extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('.bsb-page-embed__frame');
      this.fallback = this.querySelector('.bsb-page-embed__fallback');
      if (!this.frame || !this.dataset.src || this.dataset.loaded) return;

      this.controller = new AbortController();
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.scrollLength = 0;
      this.expectedFrameScroll = 0;

      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }

      // Start fetching about a screen before the section scrolls into view.
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      }, { rootMargin: '100% 0px' });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.controller?.abort();
      if (this.syncRequest) window.cancelAnimationFrame(this.syncRequest);
    }

    async load() {
      this.dataset.loaded = 'loading';
      try {
        const response = await fetch(this.dataset.src, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        this.render(new DOMParser().parseFromString(await response.text(), 'text/html'));
        this.startScrollSync();
        this.dataset.loaded = 'true';
      } catch (error) {
        console.error('[bsb-page-embed]', error);
        this.dataset.loaded = 'false';
        if (this.fallback) this.fallback.hidden = false;
      }
    }

    // document.open() gives the frame this page's URL, so root-relative assets resolve.
    render(source) {
      const main = source.querySelector('#MainContent');
      if (!main) throw new Error('The page has no #MainContent');

      const head = [...source.head.querySelectorAll(HEAD_SELECTOR)].map((node) => node.outerHTML).join('');
      const doc = this.frame.contentDocument;
      doc.open();
      doc.write(`<!doctype html><html><head>${head}</head><body><div class="content-for-layout">${main.innerHTML}</div></body></html>`);
      doc.close();

      // The theme's inline script swaps no-js for js; that script is not carried over.
      doc.documentElement.lang = source.documentElement.lang;
      doc.documentElement.className = source.documentElement.className.replace(/\bno-js\b/, 'js');
      doc.body.className = source.body.className;

      // Without its own scrollbar, wheel and touch over the frame fall through to the home page.
      doc.documentElement.style.overflow = 'hidden';
      doc.documentElement.style.scrollBehavior = 'auto';

      doc.addEventListener('click', (event) => this.handleLinkClick(event, doc), { signal: this.controller.signal });

      // Anchor jumps and scrollIntoView inside the frame are turned into home page scrolls.
      const embed = this;
      this.frame.contentWindow.Element.prototype.scrollIntoView = function () {
        embed.scrollToTarget(this);
      };
    }

    startScrollSync() {
      const frameWindow = this.frame.contentWindow;
      const options = { signal: this.controller.signal, passive: true };
      const scheduleSync = () => {
        if (this.syncRequest) return;
        this.syncRequest = window.requestAnimationFrame(() => {
          this.syncRequest = null;
          this.sync();
        });
      };

      window.addEventListener('scroll', scheduleSync, options);
      window.addEventListener('resize', () => {
        this.measure();
        scheduleSync();
      }, options);
      // Focus moving into the frame can still scroll it; the home page follows so both stay in step.
      frameWindow.addEventListener('scroll', () => this.followFrameScroll(), options);

      // Images loading and quiz steps change the embedded content's height.
      this.resizeObserver = new frameWindow.ResizeObserver(() => {
        this.measure();
        this.sync();
      });
      this.resizeObserver.observe(this.frame.contentDocument.body);

      this.measure();
      this.sync();
    }

    get pinTop() {
      return parseFloat(window.getComputedStyle(this.frame).top) || 0;
    }

    measure() {
      const content = this.frame.contentDocument.documentElement.scrollHeight;
      this.scrollLength = Math.max(0, content - this.frame.clientHeight);
      this.style.setProperty('--bsb-embed-scroll', `${this.scrollLength}px`);
    }

    sync() {
      const progress = Math.min(this.scrollLength, Math.max(0, this.pinTop - this.getBoundingClientRect().top));
      this.expectedFrameScroll = Math.round(progress);
      this.frame.contentWindow.scrollTo({ top: progress, behavior: 'instant' });
    }

    // Home page scroll position at which the frame shows the given offset of the embedded page.
    pageScrollFor(frameOffset) {
      const clamped = Math.min(this.scrollLength, Math.max(0, frameOffset));
      return this.getBoundingClientRect().top + window.scrollY - this.pinTop + clamped;
    }

    followFrameScroll() {
      const frameScroll = this.frame.contentWindow.scrollY;
      if (Math.abs(frameScroll - this.expectedFrameScroll) <= 1) return;
      this.expectedFrameScroll = Math.round(frameScroll);
      window.scrollTo({ top: this.pageScrollFor(frameScroll), behavior: 'instant' });
    }

    scrollToTarget(target) {
      const doc = this.frame.contentDocument;
      const view = doc.defaultView;
      const padding = parseFloat(view.getComputedStyle(doc.documentElement).scrollPaddingTop) || 0;
      const offset = target.getBoundingClientRect().top + view.scrollY - padding;
      window.scrollTo({ top: this.pageScrollFor(offset), behavior: this.motionQuery.matches ? 'auto' : 'smooth' });
    }

    // Storefront pages cannot load inside a frame, so links to other pages open in the main window.
    handleLinkClick(event, doc) {
      const link = event.target.closest?.('a[href]');
      if (!link || link.target) return;

      const url = new URL(link.href, doc.baseURI);
      const current = new URL(doc.URL);
      const samePage = url.origin === current.origin && url.pathname === current.pathname && url.search === current.search;
      if (samePage && url.hash) {
        const target = doc.getElementById(decodeURIComponent(url.hash.slice(1)));
        if (!target) return;
        event.preventDefault();
        this.scrollToTarget(target);
        return;
      }
      link.target = '_top';
    }
  }

  customElements.define('bsb-page-embed', BSBPageEmbed);
})();
